import { getGlobalHeadersAsObject } from "../global-headers";

/**
 * The signed-in user's customer's Jobylon setup.
 *
 * `configured: false` means this customer has never set Jobylon up. A feed that
 * is set up but switched off keeps its settings with `active: false` — every
 * part of the Jobylon UI outside the settings page stays hidden unless active.
 */
export type JobylonConfig =
	| { configured: false }
	| {
			configured: true;
			active: boolean;
			feed_url: string;
			/** This customer's Push credentials. Blank when ODI only reads
			 *  their applicants and never puts anyone on their board. */
			push_app_id: string;
			push_app_key: string;
			/** Where a grade lands on the customer's own Jobylon board. Null on
			 *  both means ODI records the grade and moves nobody. */
			stage_on_up: number | null;
			stage_on_down: number | null;
			last_polled: string | null;
			last_error: string;
	  };

/** One stage of a customer's pipeline, as Jobylon lists it. */
export interface JobylonAccountStage {
	status_id: number;
	name: string;
	/** Jobylon's own fixed enum: 0 new, 1 in progress, 19 rejected, 20 hired,
	 *  21 on hold. A customer names stages freely but cannot invent a group. */
	group: number | null;
}

export interface JobylonStageOptions {
	stages: JobylonAccountStage[];
	/** What ODI would choose if nobody picked: the one rejected stage for a no,
	 *  the first in-progress stage for a yes. */
	suggested: { stage_on_up: number | null; stage_on_down: number | null };
}

/** This customer's whole pipeline, read through to Jobylon. */
export const fetchJobylonStageOptions = async (
	signal?: AbortSignal
): Promise<JobylonStageOptions> => {
	const response = await fetch("/api/v2/jobylon-config/stages/", {
		headers: getGlobalHeadersAsObject(),
		signal,
	});
	if (!response.ok) {
		throw new Error(`Could not read the Jobylon stages (${response.status})`);
	}
	return response.json();
};

/** An open, imported role whose latest applicant came without a CV. */
export interface JobylonRoleWithoutCv {
	job_id: number;
	title: string;
	/** Null when the Jobylon job is not linked to an assignment. */
	assignment_id: number | null;
	/** When that latest application arrived. */
	latest_application: string;
}

/**
 * This customer's roles that Jobylon is not sending CVs for — each job lacks
 * Jobylon's "Partner integration" pipeline action. Empty without Jobylon.
 */
export const fetchJobylonRolesWithoutCv = async (): Promise<JobylonRoleWithoutCv[]> => {
	const response = await fetch("/api/v2/jobylon-config/missing_cv/", {
		headers: getGlobalHeadersAsObject(),
	});
	if (!response.ok) {
		throw new Error(`Could not read the Jobylon CV check (${response.status})`);
	}
	const body: { roles: JobylonRoleWithoutCv[] } = await response.json();
	return body.roles;
};

export const fetchJobylonConfig = async (
	signal?: AbortSignal
): Promise<JobylonConfig> => {
	const response = await fetch("/api/v2/jobylon-config/", {
		headers: getGlobalHeadersAsObject(),
		signal,
	});
	if (!response.ok) {
		throw new Error(`Could not read the Jobylon settings (${response.status})`);
	}
	return response.json();
};

/**
 * Saves this customer's Jobylon settings.
 *
 * The backend fetches the feed URL first and rejects one that fails. The Push
 * pair is optional, but must be given together or not at all.
 */
export const saveJobylonFeedUrl = async (
	feedUrl: string,
	pushAppId = "",
	pushAppKey = "",
	stageOnUp: number | null = null,
	stageOnDown: number | null = null
): Promise<JobylonConfig & { jobs_found: number }> => {
	const response = await fetch("/api/v2/jobylon-config/", {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			feed_url: feedUrl,
			push_app_id: pushAppId,
			push_app_key: pushAppKey,
			stage_on_up: stageOnUp,
			stage_on_down: stageOnDown,
		}),
	});
	const body = await response.json().catch(() => null);
	if (!response.ok) {
		throw new Error(
			body?.detail ?? `Could not save the feed URL (${response.status})`
		);
	}
	return body;
};

/** Switches Jobylon on or off, keeping every setting either way. */
export const setJobylonActive = async (active: boolean): Promise<JobylonConfig> => {
	const action = active ? "enable" : "disable";
	const response = await fetch(`/api/v2/jobylon-config/${action}/`, {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
	});
	if (!response.ok) {
		throw new Error(`Could not ${action} Jobylon (${response.status})`);
	}
	return response.json();
};

/** Whether Jobylon should show anywhere outside its settings page. */
export const isJobylonActive = (
	config: JobylonConfig
): config is Extract<JobylonConfig, { configured: true }> =>
	config.configured && config.active;
